import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewRounded from '@mui/icons-material/ArrowBackIosNewRounded';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import type { ActionDetailResponse, ParticipantResponse } from "../dto/actions-dto";
import { deleteActionDetailRequest, getActionDetailRequest, getParticipantListRequest, deleteParticipantRequest } from "../api/actions-api";
import Typography from '@mui/material/Typography';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Fab from "@mui/material/Fab";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import showQuestion from "../components/QuestionDialog";
import DOMPurify from "dompurify";
import Box from "@mui/material/Box";
import Participant from "../components/Participant";
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Utils from '../utils/Utils'

const ActionDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>(); 
    const [detail, setDetail] = useState<ActionDetailResponse | null>(null);
    const [participants, setParticipants] = useState<ParticipantResponse[]>([]);
    const navigate = useNavigate(); // <-- this is the hook
    const marginLeft: number = 14;

    const handleBack = () => {
        if (Utils.isActionPlanned(detail?.date))
            navigate("/actions/list/planned");
        else
            navigate("/actions/list/recent");
    };

    const addParticipant = () => {
        navigate(`/participant/new/edit/${id}`);
    };

    const editParticipant = (idParticipant: number) => {
        navigate(`/participant/${idParticipant}/edit/${id}`);
    };

    const deleteParticipant = async (idParticipant: number) => {
        const confirmed = await showQuestion(t("participant.delete.action.confirmation"));
        if (!confirmed) return;

        await deleteParticipantRequest(idParticipant);

        setParticipants(prev =>
            prev ? prev.filter(p => p.id !== Number(idParticipant)) : prev
        );
    };

    const editAction = () => {
        navigate(`/actions/${id}/edit`);
    };

    const deleteAction = async () => {
        var confirmed = await showQuestion(t("action.delete.action.confirmation"));
        if (!confirmed) return;

        confirmed = await showQuestion(t("action.delete.action.last.warning"), t("dialog.last.warning"));
        if (!confirmed) return;

        await deleteActionDetailRequest(Number(id));

        if (Utils.isActionPlanned(detail?.date))
            navigate("/actions/list/planned");
        else
            navigate("/actions/list/recent");
    };

    useEffect(() => {
        const fetchActionDetail = async () => {
            try {
                const data = await getActionDetailRequest(Number(id));
                setDetail(data);
            } catch (err: any) {
                // Global Snackbar handles error
            }
        };

        fetchActionDetail();
    }, []);

    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                const data = await getParticipantListRequest(Number(id));
                setParticipants(data);
            } catch (err: any) {
                // Global Snackbar handles error
            }
        };

        fetchParticipants();
    }, []);

    const sanitizedDescription = detail?.description ? DOMPurify.sanitize(detail.description) : "";

    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: '#fff',
        ...theme.typography.body2,
        padding: theme.spacing(1),
        textAlign: 'center',
        color: (theme.vars ?? theme).palette.text.secondary,
        ...theme.applyStyles('dark', {
            backgroundColor: '#1A2027',
        }),
    }));

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{margin: 0}}>
                <IconButton onClick={handleBack}>
                    <ArrowBackIosNewRounded />
                </IconButton>

                <h2 style={{ margin: 0 }}>{t("menu.actions")}</h2>
            </Stack>

            <p style={{marginLeft: marginLeft}}>
                <Typography
                    component="span"
                    variant="h4"
                    sx={{fontWeight: 'bold' }}
                >
                    {detail?.title}
                </Typography>
            </p>

            <p style={{marginLeft: marginLeft}}>
                <Typography
                    component="span"
                    sx={{fontWeight: 'bold' }}
                >
                    {t("actions.when")}:
                </Typography>
                <Typography
                    component="span"
                    sx={{marginLeft: 2}}
                >
                    {detail?.when}
                </Typography>

                <br />

                <Typography
                    component="span"
                    sx={{fontWeight: 'bold' }}
                >
                    {t("actions.place")}:
                </Typography>
                <Typography
                    component="span"
                    sx={{marginLeft: 2}}
                >
                    {detail?.place}
                </Typography>
                <Typography
                    component="span"
                    dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                />
            </p>

            <div style={{height: 40}} />

            <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton onClick={addParticipant}>
                    <PersonAddIcon />
                </IconButton>

                <Typography
                    component="span"
                    variant="h6"
                    sx={{fontWeight: 'bold' }}
                >
                    {t("actions.participants")}:
                </Typography>
            </Stack>

            <Box>
                <Grid container spacing={2} sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                }}>
                    {participants?.map((item, index) => (
                        <Grid key={index} sx={{ display: 'flex' }}>
                            <Item sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Participant
                                    id={Number(item.id)}
                                    image_index={item?.image_index}
                                    name={item?.name}
                                    description={item?.description}
                                    onEditClick={() => editParticipant(Number(item.id))}
                                    onDeleteClick={() => deleteParticipant(Number(item.id))}
                                />
                            </Item>
                        </Grid>
                    ))}
                </Grid>
            </Box>


            <Fab
                color="primary"
                aria-label="add"
                onClick={editAction}
                sx={{
                    position: "fixed",
                    bottom: 24,
                    right: 124
                }}
            >
                <EditIcon />
            </Fab>
            <Fab
                color="primary"
                aria-label="delete"
                onClick={deleteAction}
                sx={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    backgroundColor: 'error.main',
                }}
            >
                <DeleteIcon />
            </Fab>
        </>
    );
};

export default ActionDetail;